import React, { useState, useEffect, useContext, useRef } from 'react'
import ReactDOM from 'react-dom'
import {ErrorBoundary} from 'react-error-boundary'
import Routes from './components/Routes'
import { logout, changePassword, register, update, verifyEmail, dashboard, usePromo, affiliates } from './services/auth'
import { fire } from './services/events'
import { uploadImage as imagesUploadImage, deleteImage as imagesDeleteImage } from './services/images'
import { loadCategory, flatCategories, flatToTree, categoriesByKeywords, toggleList as toggleCategoryList } from './services/categories'
import { paymentIntent } from './services/transactions'
import { messagesStore, messagesShow, messagesIndex, messagesThreadBy, messagesUpdate, messagesUnreadCount } from './services/messages'
import { toggleList } from './services/lists'
import { companyIndex, companyStore, companyShow, companyByUser, companyUpdate, companyDelete } from './services/companies'

import {
	loadPost,
	loadPostLists,
	previewPost,
	userPosts,
	postDelete,
	postPublish,
	postUndelete,
	uploadImage,
	deleteImage,
	orderImages,
	createUpdatePost,
	validateUpdatePost,
	postVerify,
	recentBy,
	postRepost,
	postRenew,
	postImport,
	toggleList as togglePostList ,
	searchPosts,
	globalSearch,
	bulkRepost,
	bulkRenew,
	bulkDelete,
	similarPosts,
	sellersPosts,
	postPaymentIntent,
	postPurchase
} from './services/posts'
import { commentStore, commentUpdate, commentShow, commentFeed,	commentFeedAccount, commentDelete, commentRestore, commentVerify } from './services/comments'
import { bannerIndex, bannerViews, bannerClick, bannerDetails, bannerDetailsBy, bannerCreate, userBanners, bannerShow, bannerDelete, bannerPurchase, bannerUpdate, bannerPaymentIntent } from './services/banners'
import { store as saveSearch, list as savedSearchesList, deleteSearch, updateSearch } from './services/search'
import { list as pointsList, summary as pointsSummary } from './services/point'
import { leadList, leadSummary, createLead, viewLead, updateLead, deleteLead, stepLead } from './services/leads'
import AppContext, { setState } from './contexts/AppContext'
import { ToastContextProvider } from "./contexts/ToastContext";
import { HelmetProvider } from 'react-helmet-async';

const App = (props) => {

	const [ data, setData ] = useState({app: {}});

	const [ categories, setCategories ] = useState([])
	const [ countries, setCountries ] = useState([])
	const [ states, setStates ] = useState([])
	const [ cities, setCities ] = useState([])
	const [ location, setLocation ] = useState(null)

	const [ favoriteCategories, setFavoriteCategories ] = useState(null)

	const [ callbacks, _setCallbacks ] = useState([])
	const [ storageLoaded, setStorageLoaded ] = useState(false)
	const callbacksRef = useRef(callbacks);

	const setCallbacks = newCallbacks => {
		callbacksRef.current = newCallbacks;
		_setCallbacks(newCallbacks)
	}

	const mSeconds = new Date().getTime()

	const callbackProcess = event => {
		if(!event || !event.data) return null
		const { action, id, data } = event.data
		if(action == 'loaded'){
			setStorageLoaded(true)
		}
		if(action == 'callback'){
			setCallbacks([...callbacksRef.current, { id: id, data: data } ])
		}
	}

	const setAppState = (_key, _value) => {
		const key = typeof(_key) == 'string' ? _key : ''
		const value = typeof(_key) == 'string' ? _value : _key
		const _data = setState(key, value, data)
		localStorage.setItem('appState', JSON.stringify(_data))
		setData(_data)
	}

	const setUserFavoriteCategories = data => setFavoriteCategories(data)

	const access_token = data?.app?.access_token
	const user_str_id = data?.app?.user_str_id
	const user_id = data?.app?.user?.id

	const isLoggedIn = () => access_token ? true : false
	const hasAdminRole = () => isLoggedIn() && data.app?.user?.type == 'admin' ? true : false
	const hasSalesRole = () => hasAdminRole() || (isLoggedIn() && data.app?.user?.type == 'sales') ? true : false

	const postServices = {
		loadPost: post_id => loadPost(post_id, access_token, user_str_id),
		loadPostLists: post_id => loadPostLists(post_id, access_token, user_str_id),
		previewPost: post_id => previewPost(post_id, access_token, user_str_id),
		createUpdatePost: post => createUpdatePost(post, access_token, user_str_id),
		validateUpdatePost: post => validateUpdatePost(post, access_token),
		userPosts: (search, withCategories) => userPosts(search, withCategories, access_token),
		postDelete: post_id => postDelete(post_id, access_token),
		postUndelete: post_id => postUndelete(post_id, access_token),
		postPublish: post_id => postPublish(post_id, access_token),
		postRepost: post_id => postRepost(post_id, access_token),
		postRenew: post_id => postRenew(post_id, access_token),
		postImport: (type, url) => postImport(type, url, access_token),
		uploadImage: (file, post_id) => uploadImage(file, post_id, access_token),
		deleteImage: (image_id, post_id) => deleteImage(image_id, post_id, access_token),
		orderImages: (post_id, data) => orderImages(post_id, data, access_token),
		postVerify: post_id => postVerify(post_id),
		recentBy: slug => recentBy(access_token, user_str_id, slug),
		toggleList: (post_id, type, options) => togglePostList(post_id, type, options, access_token, user_str_id),
		searchPosts: (encoded, withData) => searchPosts(encoded, withData, access_token, user_str_id),
		globalSearch: query => globalSearch(query, access_token, user_str_id),
		bulkRepost: posts => bulkRepost(posts, access_token),
		bulkDelete: posts => bulkDelete(posts, access_token),
		bulkRenew: posts => bulkRenew(posts, access_token),
		similarPosts: postId => similarPosts(postId, access_token, user_str_id),
		sellersPosts: postId => sellersPosts(postId, access_token, user_str_id),
		paymentIntent: (postId, data) => postPaymentIntent(postId, data, access_token),
		purchase: (postId, method) => postPurchase(postId, method, access_token)
	}

	const categoryServices = {
		loadCategory: (slug, search, core_key, withCategory) => loadCategory(slug, search, core_key, withCategory, access_token, user_str_id),
		toggleList: (slug, type) => toggleCategoryList(slug, type, access_token, user_str_id),
		byKeywords: (keywords, exclude) => categoriesByKeywords(keywords, exclude, access_token)
	}

	const listServices = {
		toggle: options => toggleList(options, access_token),
	}

	const companyServices = {
		index: () => companyIndex(access_token),
		store: data => companyStore(data, access_token),
		show: id => companyShow(id, access_token),
		byUser: () => companyByUser(access_token),
		update: (id, data) => companyUpdate(id, data, access_token),
		delete: id => companyDelete(id, access_token),
	}

	const transctionServices = {
		intent: data => paymentIntent(data, access_token)
	}

	const messageServices = {
		store: data => messagesStore(data, access_token),
		update: (id, message) => messagesUpdate(id, message, access_token),
		index: (search, page) => messagesIndex(search, page, access_token),
		unreadCount: () => messagesUnreadCount(access_token),
		show: thread => messagesShow(thread, access_token),
		threadBy: data => messagesThreadBy(data, access_token)
	}

	const commentServices = {
		store: data => commentStore(data, access_token, user_str_id),
		update: (id, data) => commentUpdate(id, data, access_token, user_str_id),
		feed: (id, type) => commentFeed(id, type, access_token, user_str_id),
		feedAccount: (commentable_id, commentable_type, type) => commentFeedAccount(commentable_id, commentable_type, type, access_token, user_str_id),
		show: id => commentShow(id, access_token, user_str_id),
		delete: id => commentDelete(id, access_token, user_str_id),
		restore: id => commentRestore(id, access_token, user_str_id),
		verify: comment_id => commentVerify(comment_id),
	}

	const bannerServices = {
		index: data => bannerIndex(data, access_token, user_str_id),
		views: ids => bannerViews(ids, access_token, user_str_id),
		details: (post_id, placement, categories, locations, startsAt, expiresAt, isPlaceholder, canBePaid) => bannerDetails(post_id, placement, categories, locations, startsAt, expiresAt, isPlaceholder, canBePaid, access_token),
		detailsBy: banner_id => bannerDetailsBy(banner_id, access_token),
		click: id => {
			return bannerClick(id, access_token, user_str_id).then(res => {
				setAppState('app.user_str_id', res.user.str_id)
				setAppState('app.user_name', res.user.full_name)
				setAppState('app.user_has_email', res.user.email ? true : false)
				return res
			}).catch(res => res)
		},
		create: banner => bannerCreate(banner, access_token),
		user: search => userBanners(user_id, search, access_token),
		show: (id, params) => bannerShow(id, params, access_token),
		delete: id => bannerDelete(id, access_token),
		purchase: (id, method) => bannerPurchase(id, method, access_token),
		update: (id, item) => bannerUpdate(id, item, access_token),
		paymentIntent: (id, data) => bannerPaymentIntent(id, data, access_token)
	}

	const eventServices = {
		fire: (id, model, type, active) => fire(id, model, type, active, access_token, user_str_id)
	}

	const imageServices = {
		upload: (image, imageable_id, imageable_type, data) => imagesUploadImage(access_token, image, imageable_id, imageable_type, data),
		delete: image_id => imagesDeleteImage(access_token, image_id)
	}

	const searchServices = {
		save: (search_query, name, location_id, category_id) => saveSearch(search_query, name, location_id, category_id, access_token, user_str_id),
		list: search => savedSearchesList(search, access_token),
		deleteSearch: id => deleteSearch(id, access_token),
		updateSearch: (id, data) => updateSearch(id, data, access_token)
	}

	const pointServices = {
		list: search => pointsList(search, access_token),
		summary: () => pointsSummary(access_token)
	}

	const leadServices = {
		create: data => createLead(data, access_token),
		list: search => leadList(search, access_token),
		summary: () => leadSummary(access_token),
		view: id => viewLead(id, access_token),
		update: data => updateLead(data, access_token),
		delete: id => deleteLead(id, access_token),
		step: (id, data) => stepLead(id, data, access_token)
	}

	const authServices = {
		register: data => register({...data, user_str_id: user_str_id}),
		changePassword: data => changePassword(data, access_token),
		verifyEmail: () => verifyEmail(access_token),
		update: data => update(user_id, data, access_token),
		user: () => dashboard(user_id, access_token),
		affiliates: () => affiliates(user_id, access_token),
		appLogout: () => {
			logout(access_token).then(res => {
				console.log(res)
				setAppState('app.user', null)
				setAppState('app.access_token', null)
			})
		},
		appLogin: res => {
			return new Promise((resolve, reject) => {
				setFavoriteCategories(res.favoriteCategories)
				setAppState('app', {
					...data.app,
					user: res.user,
					access_token: res.access_token,
					user_str_id: res.user.str_id,
					user_name: res.user.full_name,
					user_has_email: res.user.email ? true : false,
					user_favorite_posts_count: res.user_favorite_posts_count
				})
				resolve()
			})
		},
		isLoggedIn: isLoggedIn,
		isSales: hasSalesRole,
		isAdmin: hasAdminRole,
		usePromo: key => {
			return usePromo(key, user_str_id)
		}
	}

	const value = {
		data,
		categories,
		countries,
		location,
		states,
		cities,
		setAppState,
		setUserFavoriteCategories,
		postServices,
		categoryServices,
		listServices,
		companyServices,
		favoriteCategories,
		authServices,
		searchServices,
		pointServices,
		leadServices,
		eventServices,
		commentServices,
		messageServices,
		bannerServices,
		imageServices,
		transctionServices
	}

	const isJsonValid = str => { try { JSON.parse(str) } catch (e) { return false } return true }

	const checkDataStructure = app => {
		return app
	}

	useEffect(() => {
		const mSecondsAgo = localStorage.getItem('appStateUpdated')
		const appStateJson = localStorage.getItem('appState')
		var appState = isJsonValid(appStateJson) ? JSON.parse(appStateJson) : {app: {}}
		if(!appState || !appState.app) appState = {app: {}}
		if(
			!mSecondsAgo ||
			!appStateJson ||
			!isJsonValid(appStateJson) ||
			!checkDataStructure(appState.app) ||
			!appState.isReady ||
			(process.env.MIX_LOCAL_STORAGE_EXPIRATION_MS) < (mSeconds - mSecondsAgo)
		){
			appState.isReady = true
		}

		// make sure to logout from SPA app if session authorisaion is expired

		if(!props.isLoggedIn && appState?.app?.access_token){

			// logout from spa if not logged-in in session

			if(appState?.app?.access_token){
				logout(appState?.app?.access_token)
			}
			const defaultData = {app: {}, isReady: true}
			localStorage.setItem('appState', JSON.stringify(defaultData))
			setData(defaultData)
		}else if(
			(props.isLoggedIn && !appState?.app?.access_token)
			|| (props.isLoggedIn && appState?.app?.access_token && props.userId != appState?.app?.user?.id)
		){

			// login to spa if logged-in in session

			document.addEventListener('DOMContentLoaded', function () {
			    window.livewire.on('refreshAuth', (user, redirectUrl, accessToken) => {
			        const data = JSON.parse(localStorage.getItem('appState'))
			        if(!data) data = {}
			        if(!data.app) data.app = {}
			        data.app.user = user
			        data.app.access_token = accessToken
			        localStorage.setItem('appState', JSON.stringify(data))
			        setData(data)
			    });

				window.livewire.emit('updateAuth')
			});
		}else{

			// otherwise

			localStorage.setItem('appState', JSON.stringify(appState))
			setData(appState)
		}

	}, [])

	return (
		<HelmetProvider>
			<ErrorBoundary
				FallbackComponent={ErrorScreen}
			>
				<AppContext.Provider value={value}>
					<ToastContextProvider>
						<Routes {...props} />
					</ToastContextProvider>
				</AppContext.Provider>
			</ErrorBoundary>
		</HelmetProvider>
	)
}

const ErrorScreen = ({ error }) => {

	document.title = "Something went wrong | AdSitePro"

	useEffect(() => {
		axios.post('/api/log', {
			message: error?.message,
			stack: error?.stack,
			title: document?.title,
			location: document?.location
		}, { headers: { Accept: 'application/json' }}).then(res => {
			console.log(res.data)
		}, res => {
			console.log(res.response)
		})
	}, [])

	return (
		<div className="d-flex justify-content-center flex-column" style={{ height: '100vh', width: '100vw' }}>
			<div className="text-center mx-auto" style={{maxWidth: '100%'}}>
				<div className="mb-3">
					<a href="/" title="AdSitePro">
						<img
							src="/images/logo-square.png"
							className="rounded border"
							style={{ width: '100px', height: '100px' }}
							alt="AdSitePro.com"
						/>
					</a>
				</div>
				<h1>
					Something went wrong
				</h1>
				{error?.message &&
					<p className="mb-0">System error message: “{error.message}”.</p>
				}
				<p>Administrator notified of the problem.</p>
			</div>
		</div>
	)
}

const appElement = document.getElementById("app")

const props = Object.assign({}, appElement.dataset);
Object.keys(props).map(function(key, index) {
    try {
		props[key] = JSON.parse(props[key])
    } catch(e) {
        // skip
    }
});

appElement && appElement.innerHTML !== "" ? ReactDOM.hydrate(<App {...props} />, appElement) : ReactDOM.render(<App {...props} />, appElement)
